import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

export const scrollToSection = (e, id) => {
  e.preventDefault();
  const target = document.getElementById(id);
  if (!target) return;
  if (window.__lenis) window.__lenis.scrollTo(target, { offset: -72 });
  else target.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);

  const links = [
    { id: "services", label: t.nav.services, testid: "nav-link-services" },
    { id: "work", label: t.nav.work, testid: "nav-link-work" },
    { id: "manifesto", label: t.nav.manifesto, testid: "nav-link-manifesto" },
    { id: "studio", label: t.nav.studio, testid: "nav-link-studio" },
    { id: "contact", label: t.nav.contact, testid: "nav-link-contact" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-canvas/70 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex h-20 max-w-[1600px] items-center justify-between px-4 sm:px-8 lg:px-16">
        <a
          href="#top"
          onClick={(e) => scrollToSection(e, "top")}
          className="font-display text-2xl font-extrabold uppercase tracking-tight text-ink sm:text-3xl"
          data-testid="nav-brand"
        >
          Noste<span className="text-accent">.</span>
        </a>

        <nav className="hidden items-center gap-3 lg:flex">
          {links.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              onClick={(e) => scrollToSection(e, link.id)}
              className="rounded-full bg-accent px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-canvas transition-transform duration-300 hover:scale-105"
              data-testid={link.testid}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-6 lg:flex">
          <div className="flex items-center gap-1 font-mono text-xs tracking-[0.2em]" data-testid="language-toggle-button">
            <button
              onClick={() => setLang("fi")}
              className={`px-2 py-1 transition-colors duration-300 ${lang === "fi" ? "text-accent" : "text-dim hover:text-ink"}`}
              data-testid="lang-fi-button"
            >
              FI
            </button>
            <span className="text-dim">/</span>
            <button
              onClick={() => setLang("en")}
              className={`px-2 py-1 transition-colors duration-300 ${lang === "en" ? "text-accent" : "text-dim hover:text-ink"}`}
              data-testid="lang-en-button"
            >
              EN
            </button>
          </div>
          <a
            href="#contact"
            onClick={(e) => scrollToSection(e, "contact")}
            className="group flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-canvas transition-transform duration-300 hover:scale-105"
            data-testid="nav-cta-button"
          >
            {t.nav.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <button
          className="text-ink lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-canvas/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-4 py-6 sm:px-8">
              {links.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  onClick={(e) => {
                    scrollToSection(e, link.id);
                    setOpen(false);
                  }}
                  className="py-3 font-display text-2xl font-extrabold uppercase tracking-tight text-ink transition-colors hover:text-accent"
                  data-testid={`${link.testid}-mobile`}
                >
                  {link.label}
                </a>
              ))}
              <div className="mt-4 flex items-center gap-3 font-mono text-sm tracking-[0.2em]">
                <button
                  onClick={() => setLang("fi")}
                  className={lang === "fi" ? "text-accent" : "text-dim"}
                  data-testid="lang-fi-button-mobile"
                >
                  FI
                </button>
                <span className="text-dim">/</span>
                <button
                  onClick={() => setLang("en")}
                  className={lang === "en" ? "text-accent" : "text-dim"}
                  data-testid="lang-en-button-mobile"
                >
                  EN
                </button>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
